import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n';

export default function SuperAdminHome() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState<{ installers: number; clients: number; pending: number }>({ installers: 0, clients: 0, pending: 0 });
  const [loading, setLoading] = useState(true);

  const API = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

  useEffect(() => {
    (async () => {
      try {
        const [iR, cR, sR] = await Promise.all([
          fetch(`${API}/api/admin/installers`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : { installers: [] }),
          fetch(`${API}/api/admin/clients`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : { clients: [] }),
          fetch(`${API}/api/admin/signup-requests?status=pending`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : { data: [] }),
        ]);
        setStats({
          installers: (iR.installers || []).length,
          clients: (cR.clients || []).length,
          pending: (sR.data || []).length,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token]);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Welcome */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{t('adm_home_welcome', { name: user?.firstName || t('pg_role_admin') })}</h1>
            <p className="text-sm text-slate-500 mt-0.5">{t('adm_home_subtitle')}</p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 text-sm font-medium bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors"
          >
            {t('adm_home_console_btn')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('adm_nav_installers')}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{loading ? '—' : stats.installers}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('adm_nav_clients')}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{loading ? '—' : stats.clients}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('adm_home_requests')}</p>
            {stats.pending > 0 && <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{stats.pending}</span>}
          </div>
          <p className="text-3xl font-bold text-slate-900 mt-1">{loading ? '—' : stats.pending}</p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-800">{t('adm_home_quick_access')}</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {[
            { label: t('adm_home_ql_installers'), desc: t('adm_home_ql_installers_desc'), path: '/admin/installers' },
            { label: t('adm_home_ql_clients'), desc: t('adm_home_ql_clients_desc'), path: '/admin/clients' },
            { label: t('adm_home_ql_signups'), desc: t('adm_home_ql_signups_desc'), path: '/admin/signup-requests' },
            { label: t('adm_home_ql_support'), desc: t('adm_home_ql_support_desc'), path: '/admin/support-logs' },
            { label: 'Smart Building', desc: t('adm_home_ql_smart_desc'), path: '/wattel-em' },
          ].map(item => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-blue-50/50 transition-colors text-left"
            >
              <div>
                <p className="text-sm font-medium text-slate-800">{item.label}</p>
                <p className="text-xs text-slate-400">{item.desc}</p>
              </div>
              <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
